<script setup>
import {reactive,onMounted,computed, nextTick} from 'vue';
import {ApiWrapper} from "@/Utils/ApiWrapper";
import {Permissions} from "@/Utils/Permissions";
import {ToastUtil} from "@/Utils/ToastUtil";

const props = defineProps({
  contextSchemaA: String,
  contextIdA: String
});

const data = reactive({
  openLicenseForm: false,
  dataLoading: true,
  loading: false,
  headers: [
    {
      title: 'License Number',
      align: 'start',
      sortable: true,
      key: 'licensenumber'
    },
    { title: 'Expiration', key: 'expiration', sortable: false },
    { title: 'State', key: 'state', sortable: false },
    { title: 'Actions', key: 'actions', sortable: false }
  ],
  license: [],
  isEditing: false,
  editedItem: {
    licensenumber: '',
    expiration: '',
    state: ''
  },
  defaultItem: {
    licensenumber: '',
    expiration: '',
    state: ''
  },
  states: [ 'AL', 'AK', 'AS', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FM', 'FL', 'GA', 'GU', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MH', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'MP', 'OH', 'OK', 'OR', 'PW', 'PA', 'PR', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VI', 'VA', 'WA', 'WV', 'WI', 'WY' ],
});

const csvData = computed(()=>{
  // CSV File
  return data.license.map((item) => ({
    ...item,
    licensenumber: item.licensenumber,
    expiration: item.expiration,
    state: item.state
  }));
});

const csvExport = (arrData) => {
  // CSV File
  let csvContent = 'data:text/csv;charset=utf-8,';
  csvContent += [
    Object.keys(arrData[0]).join(';'),
    ...arrData.map((item) => Object.values(item).join(';'))
  ]
      .join('\n')
      .replace(/(^\[)|(\]$)/gm, '');

  const data = encodeURI(csvContent);
  const link = document.createElement('a');
  link.setAttribute('href', data);
  link.setAttribute('download', 'export.csv');
  link.click();
}

const formatDate = (date) => {
  if (!date) return null

  return new Date(date).toLocaleDateString();
}

const listAll = () => {

  ApiWrapper.post('licensing', 'list-licenses',
      {
        personId: props.contextIdA
      })
      .then((response) => {
        if(response){
          data.license = response.licenses;
          data.license.forEach(function (arrayItem) {
            arrayItem.expiration = formatDate(arrayItem.expiration);
          });
        }
      })
      .catch((e)=>{
        ToastUtil.error(e);
      })
      .finally(()=>{
        data.dataLoading = false;
        data.loading = false;
      })
}

const editItem = (item) => {
  data.isEditing = true;
  data.editedItem = JSON.parse(JSON.stringify(item));
  data.openLicenseForm = true;
}

const deleteItem = (item) => {
  let deleteMessage = confirm('Are you sure you want to delete this item?');
  if (deleteMessage === true) {
    data.loading = true;
    ApiWrapper.post('licensing', 'delete-license', { item: item })
        .then((response) => {
          listAll();
          data.loading = false;
          ToastUtil.successMsg('Deleted license');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        })
  }
}

const close = () => {
  data.openLicenseForm = false;
  nextTick(() => {
    data.editedItem = Object.assign({}, data.defaultItem);
    data.isEditing = false;
  });
}

const save = () => {
  data.loading = true;
  if (data.isEditing) {
    ApiWrapper.post('licensing', 'edit-license', { editedItem: data.editedItem })
        .then((response) => {
          listAll();
          ToastUtil.successMsg('Updated license');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        });
  } else {
    ApiWrapper.post('licensing', 'create-license', {
          newLicense: data.editedItem,
          personId: props.contextIdA,
          // TODO Delete this
          context: {
            contextSchemaA: ''
          }
        })
        .then((response) => {
          listAll();
          ToastUtil.successMsg('Created license');
        })
        .catch((err) => {
          ToastUtil.error(err);
          data.loading = false;
        });
  }
  close();
}

onMounted(()=>{
  listAll();
})
</script>

<template>
  <v-container fluid>
    <v-card class="elevation-2" height="auto" width="auto">
      <v-toolbar flat color="white">
        <v-toolbar-title>Licenses</v-toolbar-title>
        <v-progress-linear :active="data.loading" :indeterminate="data.loading" absolute bottom color="primary">
        </v-progress-linear>
        <v-divider class="mx-4" inset vertical></v-divider>
        <v-icon @click="csvExport(csvData)" v-if="data.license.length">
          mdi-file-download
        </v-icon>

        <v-spacer></v-spacer>

        <v-btn size="small" v-if="!data.openLicenseForm && Permissions.checkPermissions(['licensing.create'])" class="primary" @click="data.openLicenseForm = true">
          New License
        </v-btn>

      </v-toolbar>

      <!-- New License Button -->
      <div v-if="data.openLicenseForm">
        <v-card-subtitle v-if="!data.isEditing && Permissions.checkPermissions(['licensing.create'])" class="primary--text">Add New License</v-card-subtitle>
        <v-card-subtitle class="primary--text">Edit License</v-card-subtitle>
        <v-row class="mx-1">
          <v-col cols="12" sm="4">
            <v-text-field v-model="data.editedItem.licensenumber" label="License Number"></v-text-field>
          </v-col>
          <v-col cols="12" sm="4">
            <v-menu
                ref="menu"
                v-model="data.menu"
                :close-on-content-click="false"
                :return-value="data.editedItem.expiration"
                transition="scale-transition"
                offset-y
                max-width="290px"
                min-width="290px"
            >
              <template v-slot:activator="{ on, props }">
                <v-text-field
                    v-model="data.editedItem.expiration"
                    label="Expiration Date"
                    hint="MM/DD/YYYY format"
                    persistent-hint
                    prepend-inner-icon="mdi-calendar"
                    v-bind="props"
                    clearable
                    v-on="on"
                    :rules="[v => !!v || 'Expiration Date is required']"
                >
                </v-text-field>
              </template>
              <v-date-picker
                  v-model="data.date"
                  scrollable
                  @update:modelValue="data.date=$event; data.editedItem.expiration = formatDate(data.date)"
              >
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="12" sm="4">
            <v-autocomplete
                v-model="data.editedItem.state"
                :items="data.states"
                label="State"
                auto-select-first
                clearable
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-card-actions class="ml-1">
          <v-btn flat color="error" @click="data.openLicenseForm = false">Cancel</v-btn>
          <v-btn flat color="primary" @click="save">Save</v-btn>
        </v-card-actions>
      </div>

      <v-data-table
          :headers="data.headers"
          :items="data.license"
          class="elevation-1"
          :loading="data.dataLoading"
          loading-text="Loading... Please wait">
        <template v-slot:[`item.actions`]="{ item }">
          <v-icon  v-if="Permissions.checkPermissions(['licensing.update'])" class="mr-2" @click="editItem(item)">
            mdi-pencil
          </v-icon>
          <v-icon v-if="Permissions.checkPermissions(['licensing.delete'])" @click="deleteItem(item)">
            mdi-delete
          </v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<style scoped>
[v-cloak] {
  display: none;
}
.container {
  padding: 5px;
}
</style>